import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { expandMirrorSelectionMembers } from '@/lib/groups/mirror-group-expansion'
import {
  isShapeMirrorEligible,
  mirrorShapeAcrossVerticalAxis,
} from '@/lib/transforms/mirror-shape-across-spine'
import { readLiveSelection } from '../helpers/live-selection'
import type { LayoutPanelProps, OnBatchShapesCommitted } from '../types/layout-panel-props'
import { commitShapeBatch } from './commit-shape-batch'

type MirrorMoveContext = {
  mgr: ShapeManagerRef | null | undefined
  mirrorSpineAxisX: number | null | undefined
  onBatchShapesCommitted: OnBatchShapesCommitted | undefined
  onMirrorMoveHoverChange: LayoutPanelProps['onMirrorMoveHoverChange']
}

/** Mirror Move: reflects the live selection (and every group descendant) across the spine. */
export function handleMirrorMove({
  mgr,
  mirrorSpineAxisX,
  onBatchShapesCommitted,
  onMirrorMoveHoverChange,
}: MirrorMoveContext): void {
  if (mirrorSpineAxisX == null) return

  const { selectedShapes, allShapes } = readLiveSelection(mgr)

  if (selectedShapes.length === 0) return

  const shapesById = new Map(allShapes.map((s) => [s.id, s]))

  if (!selectedShapes.every((s) => isShapeMirrorEligible(s, shapesById, []))) {
    return
  }

  // Fold every selected group's descendants (any nesting depth) into the flat list below — the
  // group shell and each member are mirrored independently across the same global axis. A
  // selected text frame is a shape carrying `paragraphs` (text-unification), so it's already
  // included in `shapesToMirror` — there is no separate text half anymore.
  const { shapes: shapesToMirror } = expandMirrorSelectionMembers(selectedShapes, [], allShapes, [])

  const historyTxnId = crypto.randomUUID()

  if (shapesToMirror.length > 0 && mgr && onBatchShapesCommitted) {
    const items: BatchShapeTransformItem[] = shapesToMirror.map((shape) => ({
      shapeId: shape.id,
      before: {
        x: shape.x,
        rotation: shape.rotation,
        pathCommands: shape.pathCommands,
        path: shape.path,
        photoFill: shape.photoFill,
        paragraphs: shape.paragraphs,
      },
      after: mirrorShapeAcrossVerticalAxis(shape, mirrorSpineAxisX),
    }))

    commitShapeBatch(mgr, items, onBatchShapesCommitted, { kind: 'mirror', historyTxnId })
  }

  onMirrorMoveHoverChange?.(false)
}
